import React from 'react';

interface HighlightMatchProps {
  text: string;
  query: string;
  className?: string;
}

/**
 * Renders text with query word-prefix matches highlighted.
 * Splits query into words and highlights each word where it appears
 * at the start of a word boundary in the text. Case-insensitive.
 * If no match found for a word, that word is skipped silently.
 */
export function HighlightMatch({ text, query, className }: HighlightMatchProps) {
  if (!text) return <span className={className}>{text}</span>;
  if (!query || !query.trim()) return <span className={className}>{text}</span>;

  const words = query
    .trim()
    .split(/\s+/)
    .filter(w => w.length > 0)
    .map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')); // escape regex special chars

  if (words.length === 0) return <span className={className}>{text}</span>;

  // Build a single regex that matches any query word at a word boundary start
  const pattern = new RegExp(`(${words.join('|')})`, 'gi');

  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <mark
        key={match.index}
        className="bg-amber-200/70 text-inherit rounded-sm px-0.5 not-italic"
      >
        {match[0]}
      </mark>
    );
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  if (parts.length === 0) return <span className={className}>{text}</span>;

  return <span className={className}>{parts}</span>;
}
